import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  CircleDashed,
  Download,
  FileSpreadsheet,
  FileText,
  History,
  Loader2,
  Mail,
  Pause,
  Play,
  Plus,
  RefreshCw,
  TriangleAlert,
  X,
} from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import EnTetePage from '../../components/layout/EnTetePage';
import RenduDocument from '../../components/assistants/RenduDocument';
import MissionsAssistanceTechnique from '../../components/agents/MissionsAssistanceTechnique';
import ToastContainer from '../../components/ui/ToastContainer';
import { useToast } from '../../hooks/useToast';
import { authManager } from '../../services/authManager';
import {
  type AgentCowork,
  type DocumentAgent,
  type Prerequis,
  type EtatExecution,
  type ExecutionDetail,
  type ExecutionResume,
  type Frequence,
  coworkService,
} from '../../services/api/cowork';

const dateCourte = (valeur: string | null) =>
  valeur
    ? new Date(valeur).toLocaleString('fr-FR', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    })
    : '—';

const duree = (secondes: number | null) => {
  if (secondes === null) return '';
  if (secondes < 60) return `${secondes} s`;
  const minutes = Math.round(secondes / 60);
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
};

const ETATS: Record<EtatExecution, { libelle: string; classe: string; Icone: any }> = {
  attente: { libelle: 'En attente', classe: 'text-eg-gris', Icone: CircleDashed },
  en_cours: { libelle: 'Au travail', classe: 'text-eg-accent', Icone: Loader2 },
  terminee: { libelle: 'Terminé', classe: 'text-eg-valide', Icone: CheckCircle2 },
  echouee: { libelle: 'Échec', classe: 'text-[#A33A2B]', Icone: TriangleAlert },
};

const Pastille: React.FC<{ children: React.ReactNode; sombre?: boolean }> = ({ children, sombre }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[11px] font-medium
      tracking-tight ${sombre ? 'bg-eg-ombre text-white' : 'bg-black/[0.045] text-eg-brun'}`}
  >
    {children}
  </span>
);

const EtatExecutionLigne: React.FC<{ execution: ExecutionResume }> = ({ execution }) => {
  const { libelle, classe, Icone } = ETATS[execution.etat];
  return (
    <span className={`inline-flex items-center gap-1 text-[12px] font-medium ${classe}`}>
      <Icone className={`h-3.5 w-3.5 ${execution.etat === 'en_cours' ? 'animate-spin' : ''}`} />
      {execution.etat === 'en_cours' && execution.avancement ? execution.avancement : libelle}
    </span>
  );
};

/**
 * Espace Agents : des agents qui travaillent seuls, à date fixe.
 *
 * L'agent ne fait rien de plus que l'assistant qu'il emploie ; il le fait sans
 * qu'on le lui demande, chaque mois, et range sa production ici, où toute
 * l'équipe la lit.
 */
const CoworkPage: React.FC = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, showError, showInfo } = useToast();
  const [agents, setAgents] = useState<AgentCowork[]>([]);
  const [journal, setJournal] = useState<ExecutionResume[]>([]);
  const [documents, setDocuments] = useState<DocumentAgent[]>([]);
  const [contact, setContact] = useState('');
  const [chargement, setChargement] = useState(true);
  const [ouvert, setOuvert] = useState<AgentCowork | null>(null);
  const [execution, setExecution] = useState<ExecutionDetail | null>(null);
  const [creation, setCreation] = useState(false);
  const [action, setAction] = useState(false);
  // Rapport demandé alors que les données de la période ne sont pas vérifiées.
  const [aConfirmer, setAConfirmer] = useState<{ agent: AgentCowork; prerequis: Prerequis } | null>(null);

  const estAdmin = authManager.hasRole('admin');

  const recharger = useCallback(async () => {
    const [reponse, activite] = await Promise.all([
      coworkService.listerAgents(),
      coworkService.journal().catch(() => null),
    ]);
    setAgents(reponse.agents);
    setContact(reponse.contact);
    if (activite) {
      setJournal(activite.journal);
      setDocuments(activite.documents);
    }
    return reponse.agents;
  }, []);

  useEffect(() => {
    recharger()
      .catch(() => showError('Chargement impossible', 'La bibliothèque des agents n\'a pas pu être chargée.'))
      .finally(() => setChargement(false));
  }, [recharger, showError]);

  const ouvrir = useCallback(async (agent: AgentCowork) => {
    setExecution(null);
    setOuvert(agent);
    try {
      setOuvert(await coworkService.chargerAgent(agent.id));
    } catch { /* la carte suffit en attendant */ }
  }, []);

  // Un agent au travail : on suit son avancement sans recharger à la main.
  const auTravail = useMemo(
    () => agents.some((a) => a.executions.some((e) => e.etat === 'attente' || e.etat === 'en_cours')),
    [agents],
  );
  useEffect(() => {
    if (!auTravail) return undefined;
    const minuteur = setInterval(async () => {
      const liste = await recharger().catch(() => null);
      if (liste && ouvert) {
        coworkService.chargerAgent(ouvert.id).then(setOuvert).catch(() => {});
      }
    }, 5000);
    return () => clearInterval(minuteur);
  }, [auTravail, recharger, ouvert]);

  const lancer = async (agent: AgentCowork, sansVerification = false) => {
    setAction(true);
    try {
      const resultat = await coworkService.lancer(agent.id, sansVerification);
      if ('aConfirmer' in resultat) {
        setAConfirmer({ agent, prerequis: resultat.aConfirmer });
        return;
      }
      setAConfirmer(null);
      showInfo('Agent lancé', 'Il travaille en arrière-plan ; sa production apparaîtra ici dans quelques minutes.');
      await recharger();
      if (ouvert?.id === agent.id) setOuvert(await coworkService.chargerAgent(agent.id));
    } catch (e: any) {
      showError('Lancement impossible', e.message);
    } finally {
      setAction(false);
    }
  };

  const auTravailSurAgent = (agent: AgentCowork) =>
    agent.executions.some((e) => e.etat === 'attente' || e.etat === 'en_cours');

  // L'état des données du mois, le même pour tous les agents : c'est la
  // vérification mensuelle qui le donne, et chacun travaille sur ces données.
  const donneesDuMois = agents.find((a) => a.prerequis)?.prerequis || null;

  const EtatDonnees: React.FC = () => {
    if (!donneesDuMois) return null;
    return (
      <div className="flex items-center justify-between gap-2 border-t border-black/[0.06] pt-2">
        <span className="font-mono text-[10.5px] text-eg-gris">Données {donneesDuMois.periode}</span>
        {donneesDuMois.fait ? (
          <span className="inline-flex items-center gap-1 text-[12px] font-medium text-eg-valide">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Vérifiées le {donneesDuMois.termine_le
              ? new Date(donneesDuMois.termine_le).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
              : ''}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[12px] font-medium text-eg-alerte">
            <TriangleAlert className="h-3.5 w-3.5" />
            Pas encore vérifiées
          </span>
        )}
      </div>
    );
  };

  // Réglage du calendrier dans le volet d'un agent.
  const [calendrier, setCalendrier] = useState<{ frequence: Frequence; jour: number; heure: number } | null>(null);
  useEffect(() => {
    setCalendrier(ouvert ? { frequence: ouvert.frequence, jour: ouvert.jour, heure: ouvert.heure } : null);
  }, [ouvert?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const enregistrerCalendrier = async (agent: AgentCowork) => {
    if (!calendrier) return;
    setAction(true);
    try {
      const misAJour = await coworkService.calendrier(agent.id, calendrier);
      setOuvert(misAJour);
      await recharger();
      showInfo('Calendrier enregistré', misAJour.frequence_libelle);
    } catch (e: any) {
      showError('Modification impossible', e.message);
    } finally {
      setAction(false);
    }
  };

  const basculer = async (agent: AgentCowork) => {
    setAction(true);
    try {
      const misAJour = await coworkService.basculer(agent.id);
      setOuvert(misAJour);
      await recharger();
    } catch (e: any) {
      showError('Modification impossible', e.message);
    } finally {
      setAction(false);
    }
  };

  const voir = async (resume: ExecutionResume) => {
    try {
      setExecution(await coworkService.chargerExecution(resume.id));
    } catch (e: any) {
      showError('Ouverture impossible', e.message);
    }
  };

  // Un document ou une ligne du journal ouvre directement le passage, dans
  // le volet de son agent.
  const ouvrirPassage = async (resume: ExecutionResume) => {
    const agent = agents.find((a) => a.id === resume.agent);
    if (!agent) return;
    await ouvrir(agent);
    await voir(resume);
  };

  const contenuProduction = (detail: ExecutionDetail) =>
    detail.payload?.contenu || detail.contenu || '';

  return (
    <DashboardLayout>
      <EnTetePage
        titre="Agents"
        description="Des agents qui travaillent pour le programme : ils vérifient, rédigent et rangent leur production ici, sans qu'on ait à le leur demander."
      />
      <div className="mx-auto max-w-[1040px] pb-16 pt-8">

        <h2 className="mb-3 text-[15px] font-medium text-eg-accent">Disponibles</h2>
        {chargement ? (
          <div className="flex items-center justify-center gap-2 py-16 text-[13px] text-eg-gris">
            <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {agents.map((agent) => {
              const derniere = agent.executions[0];
              return (
                <div
                  key={agent.id}
                  className="group flex flex-col rounded-2xl bg-eg-blanc p-4 text-left shadow-carte outline
                    outline-1 -outline-offset-1 outline-black/[0.07]"
                >
                <button type="button" onClick={() => ouvrir(agent)} className="flex flex-1 flex-col text-left">
                  <div className="rounded-xl bg-eg-papier px-3 py-2.5">
                    <div className="text-[11px] font-medium text-eg-estompe">Planifié</div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <Pastille sombre>
                        <CalendarClock className="h-3 w-3" />
                        {agent.frequence_libelle}
                      </Pastille>
                      <Pastille>
                        <FileText className="h-3 w-3" />
                        {agent.sortie}
                      </Pastille>
                    </div>
                    <div className="mt-2.5 flex items-center justify-between gap-2 border-t
                      border-black/[0.06] pt-2">
                      <span className="font-mono text-[10.5px] text-eg-gris">
                        {!agent.configure
                          ? 'À configurer'
                          : !agent.actif
                            ? 'Suspendu'
                            : agent.frequence === 'sur_demande'
                              ? 'Sur lancement uniquement'
                              : `Prochain : ${dateCourte(agent.prochaine_execution)}`}
                      </span>
                      {derniere && <EtatExecutionLigne execution={derniere} />}
                    </div>
                    <div className="mt-2">
                      <EtatDonnees />
                    </div>
                  </div>
                  <span className="mt-3 block text-[15px] font-medium leading-snug text-eg-encre">
                    {agent.nom}
                  </span>
                  <span className="mt-1.5 line-clamp-3 text-[13px] leading-[18px] text-eg-gris">
                    {agent.objectif}
                  </span>
                </button>
                <div className="mt-3 border-t border-eg-bordure pt-3">
                  {agent.configure && (estAdmin ? (
                    <button
                      type="button"
                      disabled={action || auTravailSurAgent(agent)}
                      onClick={() => lancer(agent)}
                      className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-eg-ombre px-3.5 py-2
                        text-[13px] font-medium text-white transition-colors hover:bg-eg-profond disabled:opacity-50"
                    >
                      {auTravailSurAgent(agent) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                      {auTravailSurAgent(agent) ? 'Au travail…' : 'Lancer maintenant'}
                    </button>
                  ) : (
                    <p className="text-[11.5px] text-eg-estompe">
                      Disponible maintenant : un administrateur peut le lancer hors calendrier.
                    </p>
                  ))}
                </div>
                </div>
              );
            })}

            <button
              type="button"
              onClick={() => setCreation(true)}
              className="flex min-h-[220px] flex-col items-center justify-center gap-2 rounded-2xl
                border border-dashed border-eg-bord p-4 text-center text-eg-gris transition-colors
                hover:border-eg-accent/50 hover:bg-eg-blanc hover:text-eg-encre"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.045]">
                <Plus className="h-4 w-4" />
              </span>
              <span className="text-[14px] font-medium">Créer un agent</span>
              <span className="max-w-[26ch] text-[12.5px] text-eg-estompe">
                Une fréquence, un objectif, une sortie : l'équipe OmnIA le met en place avec vous.
              </span>
            </button>
          </div>
        )}

        <h2 className="mb-3 mt-12 text-[15px] font-medium text-eg-accent">En cours de réalisation</h2>
        <MissionsAssistanceTechnique contact={contact} />

        {!chargement && (
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <section>
              <h2 className="mb-2.5 flex items-center gap-2 text-[15px] font-medium text-eg-accent">
                <FileText className="h-4 w-4" strokeWidth={1.8} />
                Derniers documents
              </h2>
              {documents.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-eg-bord px-4 py-8 text-center text-[12.5px] text-eg-estompe">
                  Aucun document produit pour le moment.
                </p>
              ) : (
                <div className="overflow-hidden rounded-2xl bg-eg-blanc shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07]">
                  {documents.map((document, index) => {
                    const Icone = document.format === 'tableau' ? FileSpreadsheet : FileText;
                    const exportable = document.format === 'document' || document.format === 'tableau';
                    return (
                      <div
                        key={document.id}
                        className={`flex items-center gap-3 px-4 py-3 ${index > 0 ? 'border-t border-eg-bordure' : ''}`}
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-eg-sauge/15 text-eg-accent">
                          <Icone className="h-4 w-4" strokeWidth={1.8} />
                        </span>
                        <button
                          type="button"
                          onClick={() => ouvrirPassage(document)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <div className="truncate text-[13px] font-medium text-eg-encre hover:text-eg-accent">
                            {document.titre}
                          </div>
                          <div className="mt-0.5 truncate font-mono text-[10.5px] text-eg-estompe">
                            {document.agent_nom} · {dateCourte(document.terminee_le || document.lancee_le)}
                          </div>
                        </button>
                        {exportable && (
                          <button
                            type="button"
                            onClick={() =>
                              coworkService
                                .exporter(document.id, document.titre)
                                .catch((e) => showError('Export impossible', e.message))
                            }
                            className="shrink-0 rounded-full p-1.5 text-eg-estompe hover:bg-black/[0.05] hover:text-eg-accent"
                            aria-label="Télécharger"
                            title="Télécharger"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section>
              <h2 className="mb-2.5 flex items-center gap-2 text-[15px] font-medium text-eg-accent">
                <History className="h-4 w-4" strokeWidth={1.8} />
                Journal d'activité
              </h2>
              {journal.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-eg-bord px-4 py-8 text-center text-[12.5px] text-eg-estompe">
                  Aucun agent n'a encore travaillé.
                </p>
              ) : (
                <div className="overflow-hidden rounded-2xl bg-eg-blanc shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07]">
                  {journal.map((passage, index) => (
                    <button
                      key={passage.id}
                      type="button"
                      onClick={() => ouvrirPassage(passage)}
                      className={`block w-full px-4 py-2.5 text-left hover:bg-eg-papier ${
                        index > 0 ? 'border-t border-eg-bordure' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-[92px] shrink-0 font-mono text-[10.5px] text-eg-estompe">
                          {dateCourte(passage.lancee_le)}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[12.5px] text-eg-encre">
                          {passage.agent_nom}
                        </span>
                        <EtatExecutionLigne execution={passage} />
                      </div>
                      <div className="mt-0.5 pl-[104px] text-[11px] text-eg-gris">
                        {passage.declenchement === 'manuelle' ? 'Lancé à la main' : 'Planifié'}
                        {passage.periode ? ` · ${passage.periode}` : ''}
                        {passage.duree !== null ? ` · ${duree(passage.duree)}` : ''}
                      </div>
                      {passage.erreur && (
                        <div className="mt-1 line-clamp-2 pl-[104px] text-[11.5px] text-[#8A2A2A]">
                          {passage.erreur}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>

      {/* Détail d'un agent */}
      {ouvert && (
        <div className="fixed inset-0 z-50 flex justify-end bg-eg-encre/25" onClick={() => setOuvert(null)}>
          <aside
            onClick={(e) => e.stopPropagation()}
            className="flex h-full w-full max-w-[640px] flex-col bg-eg-fond shadow-2xl"
          >
            <div className="flex items-start gap-3 border-b border-eg-bordure px-6 py-5">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap gap-1">
                  <Pastille sombre>
                    <CalendarClock className="h-3 w-3" />
                    {ouvert.frequence_libelle}
                  </Pastille>
                  <Pastille>{ouvert.sortie}</Pastille>
                  {!ouvert.actif && <Pastille>Suspendu</Pastille>}
                </div>
                <h2 className="mt-2.5 text-[19px] font-semibold tracking-tight text-eg-encre">{ouvert.nom}</h2>
              </div>
              <button
                type="button"
                onClick={() => setOuvert(null)}
                className="rounded-full p-1.5 text-eg-estompe hover:bg-black/[0.05] hover:text-eg-encre"
                aria-label="Fermer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              {execution ? (
                <div>
                  <button
                    type="button"
                    onClick={() => setExecution(null)}
                    className="mb-4 text-[12.5px] font-medium text-eg-accent hover:underline"
                  >
                    ← Tous les passages
                  </button>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[15px] font-medium text-eg-encre">
                      {execution.periode || dateCourte(execution.lancee_le)}
                    </span>
                    <EtatExecutionLigne execution={execution} />
                  </div>
                  {execution.erreur && (
                    <p className="mt-3 rounded-xl bg-[#FBF1F1] px-3.5 py-2.5 text-[13px] text-[#8A2A2A]">
                      {execution.erreur}
                    </p>
                  )}
                  {execution.a_production && (
                    <>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {(execution.format === 'document' || execution.format === 'tableau') && (
                          <button
                            type="button"
                            onClick={() =>
                              coworkService
                                .exporter(execution.id, `${execution.agent_nom} ${execution.periode}`)
                                .catch((e) => showError('Export impossible', e.message))
                            }
                            className="inline-flex items-center gap-1.5 rounded-full bg-eg-ombre px-3.5 py-1.5
                              text-[12.5px] font-medium text-white hover:bg-eg-profond"
                          >
                            <Download className="h-3.5 w-3.5" />
                            Télécharger
                          </button>
                        )}
                        {execution.peut_poursuivre && execution.conversation && (
                          <button
                            type="button"
                            onClick={() => navigate(`/workspace/conversation/${execution.conversation}`)}
                            className="inline-flex items-center gap-1.5 rounded-full bg-black/[0.045] px-3.5 py-1.5
                              text-[12.5px] font-medium text-eg-brun hover:bg-black/[0.07]"
                          >
                            Poursuivre dans une conversation
                            <ArrowRight className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="mt-4 rounded-2xl bg-eg-blanc p-5 text-[13px] shadow-carte outline
                        outline-1 -outline-offset-1 outline-black/[0.07]">
                        <RenduDocument contenu={contenuProduction(execution)} />
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-[14px] leading-relaxed text-eg-brun">{ouvert.objectif}</p>

                  <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 rounded-2xl bg-eg-papier px-4 py-3.5 text-[12.5px]">
                    <div>
                      <dt className="text-eg-estompe">Assistant employé</dt>
                      <dd className="mt-0.5 text-eg-encre">{ouvert.assistant_titre || 'À configurer'}</dd>
                    </div>
                    <div>
                      <dt className="text-eg-estompe">Prochain passage</dt>
                      <dd className="mt-0.5 font-mono text-eg-encre">
                        {ouvert.actif && ouvert.configure ? dateCourte(ouvert.prochaine_execution) : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-eg-estompe">Chiffres</dt>
                      <dd className="mt-0.5 text-eg-encre">
                        {ouvert.rafraichir_chiffres ? 'Rapatriés du système national avant chaque passage' : 'Derniers disponibles'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-eg-estompe">Classement</dt>
                      <dd className="mt-0.5 text-eg-encre">{ouvert.projet_nom || 'Bibliothèque des agents'}</dd>
                    </div>
                  </dl>

                  {donneesDuMois && <div className="mt-4 rounded-xl bg-eg-papier px-3 py-2"><EtatDonnees /></div>}
                  {estAdmin && ouvert.configure && calendrier && (
                    <div className="mt-4 rounded-2xl bg-eg-blanc p-4 shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07]">
                      <div className="text-[13px] font-medium text-eg-encre">Calendrier</div>
                      <div className="mt-3 flex flex-wrap items-end gap-3 text-[12.5px]">
                        <label className="flex flex-col gap-1 text-eg-gris">
                          Fréquence
                          <select
                            value={calendrier.frequence}
                            onChange={(e) => {
                              const frequence = e.target.value as Frequence;
                              setCalendrier({
                                ...calendrier,
                                frequence,
                                jour: frequence === 'hebdomadaire' ? Math.min(calendrier.jour, 6) : Math.max(1, Math.min(calendrier.jour, 28)),
                              });
                            }}
                            className="rounded-lg border border-eg-bord bg-eg-blanc px-2.5 py-1.5 text-eg-encre"
                          >
                            <option value="mensuelle">Chaque mois</option>
                            <option value="hebdomadaire">Chaque semaine</option>
                            <option value="quotidienne">Chaque jour</option>
                            <option value="sur_demande">Sur lancement uniquement</option>
                          </select>
                        </label>
                        {calendrier.frequence === 'mensuelle' && (
                          <label className="flex flex-col gap-1 text-eg-gris">
                            Jour du mois
                            <select
                              value={calendrier.jour}
                              onChange={(e) => setCalendrier({ ...calendrier, jour: Number(e.target.value) })}
                              className="rounded-lg border border-eg-bord bg-eg-blanc px-2.5 py-1.5 text-eg-encre"
                            >
                              {Array.from({ length: 28 }, (_, i) => i + 1).map((j) => (
                                <option key={j} value={j}>{j === 1 ? '1er' : j}</option>
                              ))}
                            </select>
                          </label>
                        )}
                        {calendrier.frequence === 'hebdomadaire' && (
                          <label className="flex flex-col gap-1 text-eg-gris">
                            Jour
                            <select
                              value={calendrier.jour}
                              onChange={(e) => setCalendrier({ ...calendrier, jour: Number(e.target.value) })}
                              className="rounded-lg border border-eg-bord bg-eg-blanc px-2.5 py-1.5 text-eg-encre"
                            >
                              {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map((j, i) => (
                                <option key={j} value={i}>{j}</option>
                              ))}
                            </select>
                          </label>
                        )}
                        {calendrier.frequence !== 'sur_demande' && (
                          <label className="flex flex-col gap-1 text-eg-gris">
                            Heure (Conakry)
                            <select
                              value={calendrier.heure}
                              onChange={(e) => setCalendrier({ ...calendrier, heure: Number(e.target.value) })}
                              className="rounded-lg border border-eg-bord bg-eg-blanc px-2.5 py-1.5 text-eg-encre"
                            >
                              {Array.from({ length: 24 }, (_, h) => h).map((h) => (
                                <option key={h} value={h}>{h} h</option>
                              ))}
                            </select>
                          </label>
                        )}
                        <button
                          type="button"
                          disabled={action}
                          onClick={() => enregistrerCalendrier(ouvert)}
                          className="ml-auto rounded-full bg-eg-ombre px-3.5 py-1.5 text-[12.5px] font-medium text-white hover:bg-eg-profond disabled:opacity-50"
                        >
                          Enregistrer
                        </button>
                      </div>
                      {calendrier.frequence === 'sur_demande' && (
                        <p className="mt-2.5 text-[12px] text-eg-estompe">
                          L'agent ne travaillera que lorsqu'on le lance avec « Lancer maintenant ».
                        </p>
                      )}
                    </div>
                  )}
                  {estAdmin && ouvert.configure && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={action}
                        onClick={() => lancer(ouvert)}
                        className="inline-flex items-center gap-1.5 rounded-full bg-eg-ombre px-3.5 py-1.5
                          text-[12.5px] font-medium text-white hover:bg-eg-profond disabled:opacity-50"
                      >
                        <Play className="h-3.5 w-3.5" />
                        Lancer maintenant
                      </button>
                      <button
                        type="button"
                        disabled={action}
                        onClick={() => basculer(ouvert)}
                        className="inline-flex items-center gap-1.5 rounded-full bg-black/[0.045] px-3.5 py-1.5
                          text-[12.5px] font-medium text-eg-brun hover:bg-black/[0.07] disabled:opacity-50"
                      >
                        {ouvert.actif ? <Pause className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" />}
                        {ouvert.actif ? 'Suspendre' : 'Remettre en service'}
                      </button>
                    </div>
                  )}

                  <h3 className="mb-2 mt-7 text-[13px] font-medium text-eg-encre">Passages</h3>
                  {ouvert.executions.length === 0 ? (
                    <p className="rounded-2xl border border-dashed border-eg-bord px-4 py-6 text-center text-[12.5px] text-eg-estompe">
                      Cet agent n'a pas encore travaillé.
                    </p>
                  ) : (
                    <div className="overflow-hidden rounded-2xl bg-eg-blanc shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07]">
                      {ouvert.executions.map((passage, index) => (
                        <button
                          key={passage.id}
                          type="button"
                          onClick={() => voir(passage)}
                          className={`flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-eg-papier ${
                            index > 0 ? 'border-t border-eg-bordure' : ''
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-medium text-eg-encre">
                              {passage.periode || dateCourte(passage.lancee_le)}
                            </div>
                            <div className="mt-0.5 font-mono text-[10.5px] text-eg-estompe">
                              {passage.declenchement === 'manuelle' ? 'Lancé à la main' : 'Planifié'} ·{' '}
                              {dateCourte(passage.lancee_le)}
                            </div>
                          </div>
                          <EtatExecutionLigne execution={passage} />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </aside>
        </div>
      )}

      {/* Rapport demandé sans vérification des données de la période */}
      {aConfirmer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-eg-encre/25 px-4" onClick={() => setAConfirmer(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[480px] rounded-2xl bg-eg-blanc p-6 shadow-2xl">
            <div className="flex items-center gap-2 text-eg-alerte">
              <TriangleAlert className="h-5 w-5" />
              <h2 className="text-[17px] font-semibold tracking-tight text-eg-encre">Données pas encore vérifiées</h2>
            </div>
            <p className="mt-3 text-[13.5px] leading-relaxed text-eg-brun">
              Les données de {aConfirmer.prerequis.periode} n'ont pas encore été vérifiées par
              l'agent « {aConfirmer.prerequis.nom} ». Le rapport peut partir quand même : il indiquera
              en tête que ses chiffres sont provisoires.
            </p>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                disabled={action}
                onClick={() => lancer(aConfirmer.agent, true)}
                className="rounded-full px-3.5 py-1.5 text-[13px] font-medium text-eg-gris hover:bg-black/[0.05] disabled:opacity-50"
              >
                Lancer quand même
              </button>
              <button
                type="button"
                disabled={action}
                onClick={() => {
                  const verification = agents.find((a) => a.id === aConfirmer.prerequis.agent);
                  setAConfirmer(null);
                  if (verification) lancer(verification);
                }}
                className="inline-flex items-center gap-1.5 rounded-full bg-eg-ombre px-3.5 py-1.5 text-[13px] font-medium text-white hover:bg-eg-profond disabled:opacity-50"
              >
                <Play className="h-3.5 w-3.5" />
                Lancer d'abord la vérification
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Création : par l'équipe OmnIA */}
      {creation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-eg-encre/25 px-4" onClick={() => setCreation(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[460px] rounded-2xl bg-eg-blanc p-6 shadow-2xl"
          >
            <h2 className="text-[18px] font-semibold tracking-tight text-eg-encre">Créer un agent</h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-eg-brun">
              Les agents sont mis en place par l'équipe OmnIA. Une consigne qui tourne seule
              chaque mois doit être écrite avec soin : elle ne peut pas poser de questions, et
              chaque passage est facturé au programme.
            </p>
            <p className="mt-3 text-[13.5px] leading-relaxed text-eg-brun">
              Prenez contact avec l'équipe OmnIA en précisant :
            </p>
            <ul className="mt-2 space-y-1.5 text-[13px] text-eg-gris">
              <li><span className="font-medium text-eg-encre">La fréquence</span> : chaque jour, chaque semaine, chaque mois, à quelle date.</li>
              <li><span className="font-medium text-eg-encre">L'objectif</span> : ce que l'agent doit vérifier ou rédiger.</li>
              <li><span className="font-medium text-eg-encre">La sortie</span> : note, rapport Word, tableau, et à qui elle sert.</li>
            </ul>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setCreation(false)}
                className="rounded-full px-3.5 py-1.5 text-[13px] font-medium text-eg-gris hover:bg-black/[0.05]"
              >
                Fermer
              </button>
              {contact ? (
                <a
                  href={`mailto:${contact}?subject=${encodeURIComponent('OmnIA, demande de nouvel agent')}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-eg-ombre px-3.5 py-1.5
                    text-[13px] font-medium text-white hover:bg-eg-profond"
                >
                  <Mail className="h-3.5 w-3.5" />
                  Écrire à l'équipe OmnIA
                </a>
              ) : (
                <span className="self-center text-[12.5px] text-eg-estompe">
                  Passez par l'administrateur de la plateforme.
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </DashboardLayout>
  );
};

export default CoworkPage;
